/* Paintly — one durable id per browser, so a customer's designs can be found again.

   Why this is its own file rather than a few lines pasted into each generator: three
   generator sections need it today and the designs page needs the same id tomorrow, and the
   last time a small piece of logic was reimplemented per surface (the wishlist card) it
   drifted far enough to show wrong prices for months. One implementation, one key.

   The worker treats the id as a bearer secret: whoever has it can list that browser's
   designs. So it is random, it is never put in a URL, and it is never sent anywhere except
   the generator's own endpoint.

   ── Why there is a cookie as well ──────────────────────────────────────────────────────
   This used to return an empty string wherever localStorage was blocked, and the worker then
   filed that customer's designs under the page's session id, which is a fresh random value on
   every page load. Nothing can ever ask for those again. Measured on 17 and 18 August: 22 such
   buckets holding 72 designs, about one generation in ten. A store whose visitors arrive from
   Instagram and Facebook sees a lot of in-app browsers, and those are exactly the ones that
   partition or refuse localStorage while still keeping cookies.

   So the id now lives in both places and either can rebuild the other. Order of preference:

     localStorage   the durable one, survives everything short of the customer clearing it
     cookie         first-party, 400 days asked for, which Safari will shorten to seven for a
                    cookie written by script; still far better than one page load
     neither        return empty, exactly as before, and the worker falls back to the session

   A private window still loses both when it closes, which no amount of storage can fix. */
(function () {
  var KEY = 'ply_owner_id';
  var CKEY = 'ply_oid';
  var SHAPE = /^[a-z0-9]{16,64}$/;
  var DAYS = 400;

  function mint() {
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
      }
      if (window.crypto && crypto.getRandomValues) {
        var a = new Uint8Array(16);
        crypto.getRandomValues(a);
        var out = '';
        for (var i = 0; i < a.length; i++) out += ('0' + a[i].toString(16)).slice(-2);
        return out;
      }
    } catch (e) {}
    var s = '';
    for (var j = 0; j < 32; j++) s += '0123456789abcdef'[Math.floor(Math.random() * 16)];
    return s;
  }

  function readCookie() {
    try {
      var all = String(document.cookie || '').split(';');
      for (var i = 0; i < all.length; i++) {
        var p = all[i].trim();
        if (p.indexOf(CKEY + '=') === 0) return decodeURIComponent(p.slice(CKEY.length + 1));
      }
    } catch (e) {}
    return '';
  }

  function writeCookie(v) {
    try {
      document.cookie = CKEY + '=' + encodeURIComponent(v) +
        ';path=/;max-age=' + (DAYS * 86400) + ';SameSite=Lax' +
        (location.protocol === 'https:' ? ';Secure' : '');
    } catch (e) {}
  }

  function readLocal() {
    try {
      var v = localStorage.getItem(KEY);
      return v && SHAPE.test(v) ? v : '';
    } catch (e) {
      return '';
    }
  }

  /* Written, then read back. `setItem` throwing is the obvious failure, but a browser in a
     restricted mode can also accept the write and hand back nothing, and a value that is not
     really there is worse than an honest empty string. */
  function writeLocal(v) {
    try {
      localStorage.setItem(KEY, v);
      return localStorage.getItem(KEY) === v;
    } catch (e) {
      return false;
    }
  }

  function id() {
    var local = readLocal();
    var cookie = readCookie();

    if (local) {
      if (cookie !== local) writeCookie(local);   // so it can be rebuilt if localStorage is cleared
      return local;
    }
    if (cookie && SHAPE.test(cookie)) {
      writeLocal(cookie);                          // put it back where it belongs, if that works
      return cookie;
    }

    var made = mint();
    var kept = writeLocal(made);
    writeCookie(made);
    if (!kept && readCookie() !== made) return '';  // nothing here will hold it
    return made;
  }

  /* ── designs that were still being made when the tab closed ──────────────────────────────
     A design is recorded against its owner when the browser sees the job finish, which is 30 to 60
     seconds after it starts. Close the tab in that window and the picture is made and stored and
     nobody knows whose it was.

     So the job ids are written down when they start and read back on the next page load. One poll
     of the worker is enough: the poll route is what records a finished design, and it is the same
     call the page would have made itself. The worker keeps a job for three days, so anything older
     than that is dropped rather than asked about.

     This runs on any page that loads this file, which is the three generators and the Studio, so a
     customer who closes the tab and comes back anywhere gets their design back. */
  var PEND = 'ply_pending';
  var PEND_MAX_AGE = 3 * 24 * 60 * 60 * 1000;

  function remember(ids, api) {
    try {
      var clean = (ids || []).filter(function (x) { return typeof x === 'string' && x.length > 6; })
        .slice(0, 4);
      if (!clean.length) { localStorage.removeItem(PEND); return; }
      localStorage.setItem(PEND, JSON.stringify({ i: clean, a: String(api || ''), t: Date.now() }));
    } catch (e) {}
  }

  function recover() {
    var row = null;
    try {
      var raw = localStorage.getItem(PEND);
      if (!raw) return;
      row = JSON.parse(raw);
      localStorage.removeItem(PEND);          // one attempt, whatever happens
    } catch (e) { return; }
    if (!row || !row.i || !row.i.length || !row.a) return;
    if (!(Date.now() - (row.t || 0) < PEND_MAX_AGE)) return;
    if (!/^https:\/\/[a-z0-9.-]+$/i.test(row.a)) return;
    try {
      fetch(row.a + '/api/poll', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobIds: row.i }),
      }).catch(function () {});
    } catch (e) {}
  }

  /* ── the pack code ────────────────────────────────────────────────────────────────────────
     A second key that more than one surface reads: the generator's paywall writes it after a
     redemption, and the Studio needs to write it when it finds the pack on the customer's account.
     It lives here for the same reason the browser id does, so there is one place that knows the
     name of the key and what shape a code is. */
  var CREDIT = 'plyCreditCode';
  var CODE_SHAPE = /^PAINT-[A-Z0-9]{8}$/;

  function credit() {
    try {
      var v = localStorage.getItem(CREDIT);
      return v && CODE_SHAPE.test(v) ? v : '';
    } catch (e) {
      return '';
    }
  }

  function setCredit(code) {
    var v = typeof code === 'string' ? code.trim().toUpperCase() : '';
    if (!CODE_SHAPE.test(v)) return false;
    try {
      var previous = credit();
      localStorage.setItem(CREDIT, v);
      var saved = localStorage.getItem(CREDIT) === v;
      if (saved && previous !== v) window.dispatchEvent(new CustomEvent('paintly:credit-change'));
      return saved;
    } catch (e) {
      return false;
    }
  }

  window.PaintlyOwner = { id: id, key: KEY, cookieKey: CKEY, remember: remember, recover: recover,
                          credit: credit, setCredit: setCredit, creditKey: CREDIT };

  /* after the page has done its own work, not in competition with it */
  try {
    if (document.readyState === 'complete') setTimeout(recover, 1200);
    else window.addEventListener('load', function () { setTimeout(recover, 1200); });
  } catch (e) {}
})();
